import { useEffect, useState } from "react";
import { useFirestore } from "react-redux-firebase";
import { FIRESTORE_PURCHASE_ORDER_SHIPMENTS_TABLE } from "../../constants/FirestoreConstant";

/**
 * Hook to fetch and manage purchase order shipments from Firestore
 * @param {Object} options - Configuration options
 * @param {boolean} options.realtime - Whether to use real-time listener (default: true)
 * @param {string} options.status - Filter by status (optional)
 * @param {string} options.supplierId - Filter by supplier ID (optional)
 * @returns {Object} - Shipments data and state
 */
export const usePurchaseOrderShipments = (options = {}) => {
  const { realtime = true, status = null, supplierId = null } = options;
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const firestore = useFirestore();

  useEffect(() => {
    setLoading(true);
    let query = firestore.collection(FIRESTORE_PURCHASE_ORDER_SHIPMENTS_TABLE);

    // Apply filters
    if (status) {
      query = query.where("status", "==", status);
    }
    if (supplierId) {
      query = query.where("supplierId", "==", supplierId);
    }

    // Order by creation date
    query = query.orderBy("createdAt", "desc");

    if (realtime) {
      const unsubscribe = query.onSnapshot(
        (snapshot) => {
          const data = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setShipments(data);
          setLoading(false);
          setError(null);
        },
        (err) => {
          console.error("Error fetching purchase order shipments:", err);
          setError(err);
          setLoading(false);
        },
      );

      return () => unsubscribe();
    } else {
      query
        .get()
        .then((snapshot) => {
          const data = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setShipments(data);
          setLoading(false);
        })
        .catch((err) => {
          console.error("Error fetching purchase order shipments:", err);
          setError(err);
          setLoading(false);
        });
    }
  }, [firestore, realtime, status, supplierId]);

  return { shipments, loading, error };
};

/**
 * Hook to fetch a single purchase order shipment with a real-time listener for
 * its history subcollection.
 * @param {string} id - The shipment document ID
 * @returns {Object} - { shipment, loading, shipmentHistory, historyLoading, error }
 */
export const usePurchaseOrderShipment = (id) => {
  const firestore = useFirestore();
  const [shipment, setShipment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shipmentHistory, setShipmentHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    const unsubscribe = firestore
      .collection(FIRESTORE_PURCHASE_ORDER_SHIPMENTS_TABLE)
      .doc(id)
      .onSnapshot(
        (doc) => {
          setShipment(doc.exists ? { id: doc.id, ...doc.data() } : null);
          setLoading(false);
        },
        (err) => {
          console.error("Error listening to shipment:", err);
          setError(err);
          setLoading(false);
        },
      );
    return () => unsubscribe();
  }, [firestore, id]);

  useEffect(() => {
    if (!id) return;
    const unsubscribe = firestore
      .collection(FIRESTORE_PURCHASE_ORDER_SHIPMENTS_TABLE)
      .doc(id)
      .collection("history")
      .onSnapshot(
        (snapshot) => {
          const sorted = snapshot.docs
            .map((doc) => ({ id: doc.id, ...doc.data() }))
            .sort((a, b) => (b.changedAt || 0) - (a.changedAt || 0));
          setShipmentHistory(sorted);
          setHistoryLoading(false);
        },
        (err) => {
          console.error("Error loading shipment history:", err);
          setHistoryLoading(false);
        },
      );
    return () => unsubscribe();
  }, [firestore, id]);

  return { shipment, loading, shipmentHistory, historyLoading, error };
};
